import { h, mount } from "@/lib/dom";
import { icon } from "@/lib/icons";
import { button, field, textInput, toast } from "@/components/ui";
import { auth, pbErrorMessage, type OAuthProvider } from "@/services";
import { oauthBrand } from "@/lib/oauthBrands";
import { navigate } from "@/app/router";
import { config } from "@/config";

/** Login + Registrierung (umschaltbar) inkl. OAuth. Beispiel-Datenfluss zu PocketBase. */
export function LoginPage(): HTMLElement {
  const container = h("div", { class: "auth-wrap" });
  let mode: "login" | "register" = "login";
  let providers: OAuthProvider[] = [];

  function render() {
    const isLogin = mode === "login";

    const email = textInput({
      type: "email",
      placeholder: "du@example.com",
      autocomplete: "email",
      required: true,
    });
    const password = textInput({
      type: "password",
      placeholder: "Passwort (min. 8 Zeichen)",
      autocomplete: isLogin ? "current-password" : "new-password",
      required: true,
    });

    const errorBox = h("div", { class: "field-error", style: "min-height:1.2em" });
    const submitBtn = button({
      label: isLogin ? "Anmelden" : "Konto erstellen",
      variant: "primary",
      type: "submit",
      block: true,
    });

    const form = h(
      "form",
      {
        onsubmit: async (e: Event) => {
          e.preventDefault();
          errorBox.textContent = "";
          submitBtn.disabled = true;
          try {
            if (isLogin) {
              await auth.login(email.value, password.value);
            } else {
              // Registrierung bewusst kurz: E-Mail und Passwort, sonst nichts.
              await auth.register({
                email: email.value,
                password: password.value,
                passwordConfirm: password.value,
              });
            }
            toast(isLogin ? "Willkommen zurück!" : "Konto erstellt.", "success");
            navigate("/");
          } catch (err) {
            errorBox.textContent = pbErrorMessage(err);
          } finally {
            submitBtn.disabled = false;
          }
        },
      },
      field("E-Mail", email),
      field("Passwort", password),
      errorBox,
      submitBtn
    );

    /** Ein Anbieter-Button (Logo + Beschriftung), startet den OAuth2-Flow. */
    function oauthButton(p: OAuthProvider): HTMLElement {
      const brand = oauthBrand(p);
      const btn = h(
        "button",
        {
          type: "button",
          class: `btn btn-block oauth-btn ${brand.className}`.trim(),
          onclick: async () => {
            btn.disabled = true;
            errorBox.textContent = "";
            try {
              await auth.loginWithOAuth(p.name);
              toast("Willkommen!", "success");
              navigate("/");
            } catch (err) {
              // Abbruch im Popup ist kein Fehler, den man anzeigen muss.
              const msg = pbErrorMessage(err);
              if (!/abgebrochen|cancel|closed/i.test(msg)) errorBox.textContent = msg;
            } finally {
              btn.disabled = false;
            }
          },
        },
        h("span", { class: "oauth-logo", html: brand.svg }),
        h("span", { class: "oauth-label" }, brand.label)
      );
      return btn;
    }

    // OAuth-Buttons (nur falls in PocketBase Provider konfiguriert sind)
    const oauthSection =
      providers.length > 0
        ? h(
            "div",
            null,
            h("div", { class: "auth-oauth" }, ...providers.map(oauthButton)),
            h("div", { class: "auth-divider" }, h("span", null, "oder mit E-Mail"))
          )
        : null;

    const toggle = h(
      "p",
      { class: "muted text-sm mt-2", style: "text-align:center" },
      isLogin ? "Noch kein Konto? " : "Schon registriert? ",
      h(
        "a",
        {
          href: "#",
          onclick: (e: Event) => {
            e.preventDefault();
            mode = isLogin ? "register" : "login";
            render();
          },
        },
        isLogin ? "Jetzt registrieren" : "Zum Login"
      )
    );

    const card = h(
      "div",
      { class: "card auth-card" },
      h("div", { class: "auth-logo", html: icon("book", 28) }),
      h("h1", { class: "auth-title" }, config.appName),
      h(
        "p",
        { class: "auth-sub" },
        isLogin
          ? "Melde dich an, um weiterzulernen."
          : "Erstelle ein Konto, um loszulegen."
      ),
      oauthSection,
      form,
      toggle
    );

    mount(container, card);
  }

  render();
  // OAuth-Provider asynchron nachladen und ggf. neu rendern.
  auth
    .listOAuthProviders()
    .then((p) => {
      if (p.length > 0) {
        providers = p;
        render();
      }
    })
    .catch(() => {
      /* ignorieren – dann eben nur E-Mail-Login */
    });

  return container;
}
